import React, { forwardRef } from 'react';
import { User, ChevronDown, CreditCard, Settings, HelpCircle, LogOut, PlayCircle } from 'lucide-react';
import type { User as UserType } from '../../types';

function getDisplayName(user: UserType | null | undefined): string {
  if (!user) return 'User';
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return user.email?.split('@')[0] || 'User';
}

interface HeaderUserMenuProps {
  show: boolean;
  onToggle: () => void;
  user: UserType | null | undefined;
  onNavigate: (path: string) => void;
  onStartTour: () => void;
  onLogout: () => void;
  pathname: string;
  zIndex: number;
}

const HeaderUserMenu = forwardRef<HTMLDivElement, HeaderUserMenuProps>(({
  show,
  onToggle,
  user,
  onNavigate,
  onStartTour,
  onLogout,
  pathname,
  zIndex,
}, ref) => {
  const displayName = getDisplayName(user);
  return (
  <div ref={ref} className="relative">
    <button
      onClick={onToggle}
      className="flex items-center space-x-2 p-2 rounded-md text-white hover:text-cyan-400 hover:bg-white/10 transition-colors touch-manipulation"
      aria-label="User menu"
    >
      <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
        <User className="w-4 h-4 text-white" />
      </div>
      <span className="text-sm font-medium hidden sm:block">{displayName}</span>
      <ChevronDown className="w-4 h-4 hidden sm:block" />
    </button>
    {show && (
      <div
        className="absolute right-0 -mt-1 w-48 lg:w-56 bg-gray-900/95 backdrop-blur-md rounded-md shadow-lg border border-white/20 z-50"
        style={{ zIndex }}
      >
        <div className="p-3 border-b border-white/20">
          <p className="text-sm font-medium text-white truncate">{user?.email}</p>
        </div>
        <div className="py-1">
          <button
            onClick={() => { onNavigate('/subscription'); onToggle(); }}
            className="flex items-center w-full px-3 py-2 text-sm text-white hover:bg-white/10 touch-manipulation"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Subscription
          </button>
          <button
            onClick={() => { onNavigate('/settings'); onToggle(); }}
            className="flex items-center w-full px-3 py-2 text-sm text-white hover:bg-white/10 touch-manipulation"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </button>
          <button
            onClick={() => { onNavigate('/support'); onToggle(); }}
            className="flex items-center w-full px-3 py-2 text-sm text-white hover:bg-white/10 touch-manipulation"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Help & Support
          </button>
          <button
            onClick={() => {
              onToggle();
              if (pathname === '/dashboard') {
                setTimeout(onStartTour, 100);
              } else {
                onNavigate('/dashboard');
                setTimeout(onStartTour, 600);
              }
            }}
            className="flex items-center w-full px-3 py-2 text-sm text-white hover:bg-white/10 touch-manipulation"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Show Tour Guide
          </button>
        </div>
        <div className="border-t border-white/20 py-1">
          <button
            onClick={() => { onLogout(); onToggle(); }}
            className="flex items-center w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 touch-manipulation"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>
      </div>
    )}
  </div>
  );
});

HeaderUserMenu.displayName = 'HeaderUserMenu';

export default HeaderUserMenu;
